import type { LiveStatusSummaryResource, KubernetesMachineLiveStatusResource, KubernetesLiveManifestDiff, ProjectGroupLiveStatusResource, ProjectLiveStatusResource, KubernetesEventResource, ContainerLogLineResource, MonitorErrorResource, KubernetesLiveStatusDetailedResource, } from "..";
import type { Client, RequestContext } from "../client";
export class KubernetesLiveStatusRepository {
    private client: Client;
    constructor(client: Client, private readonly requestContext: RequestContext) {
        this.client = client;
    }
    getLiveStatus(projectId: string, environmentId: string, tenantId?: string, summaryOnly: boolean = false): Promise<GetLiveStatusResponse> {
        const queryParams = summaryOnly ? "?summaryOnly=true" : "";
        if (tenantId === undefined) {
            return this.client.get<GetLiveStatusResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/livestatus${queryParams}`, {
                projectId: projectId,
                environmentId: environmentId,
            });
        }
        return this.client.get<GetLiveStatusResponse>(this.requestContext, `~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/livestatus${queryParams}`, {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
        });
    }
    getResource(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string): Promise<GetResourceResponse> {
        if (tenantId === undefined) {
            return this.client.get<GetResourceResponse>(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/machines/{machineId}/resources/{desiredOrKubernetesMonitoredResourceId}", {
                projectId: projectId,
                environmentId: environmentId,
                machineId: machineId,
                desiredOrKubernetesMonitoredResourceId: resourceId,
            });
        }
        return this.client.get<GetResourceResponse>(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/machines/{machineId}/resources/{desiredOrKubernetesMonitoredResourceId}", {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
            machineId: machineId,
            desiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getResourceManifest(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string): Promise<GetResourceManifestResponse> {
        if (tenantId === undefined) {
            return this.client.get<GetResourceManifestResponse>(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/untenanted/machines/{machineId}/resources/{desiredOrKubernetesMonitoredResourceId}/manifest", {
                projectId: projectId,
                environmentId: environmentId,
                machineId: machineId,
                desiredOrKubernetesMonitoredResourceId: resourceId,
            });
        }
        return this.client.get<GetResourceManifestResponse>(this.requestContext, "~/api/spaces/{spaceId}/projects/{projectId}/environments/{environmentId}/tenants/{tenantId}/machines/{machineId}/resources/{desiredOrKubernetesMonitoredResourceId}/manifest", {
            projectId: projectId,
            environmentId: environmentId,
            tenantId: tenantId,
            machineId: machineId,
            desiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getProjectDashboardLiveStatus(projectId: string): Promise<GetProjectDashboardLiveStatusBffResponse> {
        return this.client.get<GetProjectDashboardLiveStatusBffResponse>(this.requestContext, "~/bff/spaces/{spaceId}/projects/{projectId}/livestatus", {
            projectId: projectId,
        });
    }
    getProjectsDashboardLiveStatus(projectOrGroupPartialName: string | undefined): Promise<GetProjectsDashboardLiveStatusBffResponse> {
        return this.client.get(this.requestContext, "~/bff/spaces/{spaceId}/projects/livestatus{?projectOrGroupPartialName}", { projectOrGroupPartialName });
    }
    beginContainerLogsSession(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string, podName: string, containerName: string, showPreviousContainer: boolean): Promise<BeginSessionResponse> {
        return this.client.post<BeginSessionResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/logs/sessions", {
            ProjectId: projectId,
            EnvironmentId: environmentId,
            TenantId: tenantId,
            MachineId: machineId,
            PodName: podName,
            ContainerName: containerName,
            ShowPreviousContainer: showPreviousContainer,
            DesiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getContainerLogs(sessionId: string): Promise<GetContainerLogsResponse> {
        return this.client.get<GetContainerLogsResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/logs/sessions/{sessionId}", {
            sessionId,
        });
    }
    beginResourceEventsSession(projectId: string, environmentId: string, tenantId: string | undefined, machineId: string, resourceId: string): Promise<BeginSessionResponse> {
        return this.client.post<BeginSessionResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/events/sessions", {
            ProjectId: projectId,
            EnvironmentId: environmentId,
            TenantId: tenantId,
            MachineId: machineId,
            DesiredOrKubernetesMonitoredResourceId: resourceId,
        });
    }
    getResourceEvents(sessionId: string): Promise<GetResourceEventsResponse> {
        return this.client.get<GetResourceEventsResponse>(this.requestContext, "~/api/spaces/{spaceId}/observability/events/sessions/{sessionId}", {
            sessionId,
        });
    }
}
export interface GetLiveStatusResponse {
    MachineStatuses: KubernetesMachineLiveStatusResource[];
    Summary: LiveStatusSummaryResource;
}
export interface GetResourceResponse {
    Resource: KubernetesLiveStatusDetailedResource;
}
export interface GetResourceManifestResponse {
    LiveManifest: string;
    DesiredManifest: string | null;
    Diff: KubernetesLiveManifestDiff | null;
}
export interface GetProjectsDashboardLiveStatusBffResponse {
    LiveStatusAvailableForSpace: boolean;
    ProjectGroups: ProjectGroupLiveStatusResource[];
}
export interface GetProjectDashboardLiveStatusBffResponse {
    ProjectLiveStatus: ProjectLiveStatusResource;
}
export interface BeginSessionResponse {
    SessionId: string;
}
export interface GetContainerLogsResponse {
    Logs: ContainerLogLineResource[];
    IsSessionCompleted: boolean;
    Error: MonitorErrorResource | undefined;
}
export type GetResourceEventsResponse = {
    Events: KubernetesEventResource[];
    IsSessionCompleted: boolean;
    Error: MonitorErrorResource | undefined;
};
